import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId } from 'react';
import { useIntl } from 'react-intl';
import { usePreferencesRead } from '~/backend/connector-next-api/connector-next-components';
import { queryKeyFn } from '~/backend/connector-next-api/connector-next-context';
import { putUserPreferences } from '~/backend/connector-next-api/userPreferencesClient';
import {
    resolveStartupView,
    STARTUP_VIEW_LABELS,
    STARTUP_VIEWS,
} from '~/hooks/workspace/startupView';
import { enqueueSnackbar } from '~/utils/notifications';

/** Where the generated read keeps the caller's preferences. */
const PREFERENCES_KEY = queryKeyFn({
    path: '/api/preferences/me',
    operationId: 'preferencesRead',
    variables: {},
});

/**
 * The caller's choice of the view the patient list opens on, saved as soon as
 * it is picked, over the version it was read at.
 */
export default function StartupViewSelector() {
    const intl = useIntl();
    const t = (id: string) => intl.formatMessage({ id });
    const { data: preferences } = usePreferencesRead({});
    const queryClient = useQueryClient();
    const save = useMutation({
        mutationFn: ({ view, version }: { view: string; version: string }) =>
            putUserPreferences(view, version),
        // The write answers with the preferences as stored, version included.
        onSuccess: (stored) => {
            queryClient.setQueryData(PREFERENCES_KEY, stored);
            enqueueSnackbar(t('configuration.mine.saved'), {
                variant: 'success',
            });
        },
        onError: () => {
            void queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY });
            enqueueSnackbar(t('configuration.mine.saveFailed'), {
                variant: 'error',
            });
        },
    });
    const version = preferences?.version;
    const labelId = useId();

    // Labelled above the field, like the other settings beside it.
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography id={labelId} fontSize={13}>
                {t('configuration.mine.startupView')}
            </Typography>
            <TextField
                select
                size="small"
                fullWidth
                SelectProps={{ labelId }}
                value={resolveStartupView(preferences?.startupView)}
                disabled={version == null || save.isPending}
                onChange={(event) => {
                    if (version != null) {
                        save.mutate({ view: event.target.value, version });
                    }
                }}
            >
                {STARTUP_VIEWS.map((view) => (
                    <MenuItem key={view} value={view}>
                        {intl.formatMessage({ id: STARTUP_VIEW_LABELS[view] })}
                    </MenuItem>
                ))}
            </TextField>
        </Box>
    );
}
